import { useEffect, useState } from 'react';

import { ManagedAttributesService } from '../../index';
import { meetupProvider } from '../data/meetup-provider';

export interface MeetupTaskData {
	taskId: number;
	taskTitle: string;
	driver: string;
	meetupTitle: string;
}

const useMeetup = (taskIds: number[]) => {
	const [meetupTaskData, setMeetupTaskData] = useState<Map<number, MeetupTaskData> | null>(null);

	useEffect(() => {
		let isMounted = true;
		let unsubscribeTaskManagedAttributesUpdate: (() => void) | null = null;

		const buildTaskIdToRelatedTaskIdMap = () => {
			const map = new Map<number, number>();
			taskIds.forEach(taskId => {
				const relatedTaskId = ManagedAttributesService.getAttributeByName(taskId, 'TaskTemplateRelatedTaskId');
				if (relatedTaskId) {
					map.set(Number(relatedTaskId), taskId);
				}
			});
			return map;
		};

		const taskIdToRelatedTaskIdMap = buildTaskIdToRelatedTaskIdMap();
		const relatedTaskIds = Array.from(taskIdToRelatedTaskIdMap.keys());

		const managedAttribute = ManagedAttributesService.getAll()?.attributes?.task?.find(
			attribute => attribute.identifier === 'TaskTemplateRelatedTaskId'
		);

		const loadMeetup = async (_relatedTaskIds: number[], _taskIdToRelatedTaskIdMap: Map<number, number>) => {
			const meetupData = await Promise.all(
				_relatedTaskIds.map(id => meetupProvider.loadData({ taskId: Number(id), withDriver: true }))
			);

			if (isMounted) {
				setMeetupTaskData(prevData => {
					const newMap = new Map(prevData || []);

					meetupData.forEach(data => {
						const originalTaskId = _taskIdToRelatedTaskIdMap.get(data.taskId);
						if (originalTaskId) {
							newMap.set(originalTaskId, {
								...data,
								meetupTitle: managedAttribute?.display_name || ''
							});
						}
					});

					return newMap.size > 0 ? newMap : null;
				});
			}
		};

		if (relatedTaskIds.length) {
			void loadMeetup(relatedTaskIds, taskIdToRelatedTaskIdMap);
		}

		if (taskIds.length && !unsubscribeTaskManagedAttributesUpdate) {
			unsubscribeTaskManagedAttributesUpdate = ManagedAttributesService.onUpdate(event => {
				if (taskIds.includes(event.task_id) && event.name === 'TaskTemplateRelatedTaskId') {
					// Rebuild the map to get the latest related task IDs
					const updatedMap = buildTaskIdToRelatedTaskIdMap();
					const newRelatedTaskId = event.value ? Number(event.value) : null;

					if (newRelatedTaskId) {
						// Load meetup data for the new/updated related task ID
						void loadMeetup([newRelatedTaskId], updatedMap);
					} else {
						// If the value is null/empty, remove the meetup data for this task
						setMeetupTaskData(prevData => {
							if (!prevData) return null;
							const newMap = new Map(prevData);
							newMap.delete(event.task_id);
							return newMap.size > 0 ? newMap : null;
						});
					}
				}
			});
		}

		return () => {
			isMounted = false;
			unsubscribeTaskManagedAttributesUpdate?.();
		};
	}, [taskIds]);

	return meetupTaskData;
};

export default useMeetup;
